import MercureListener        from '@/services/MercureListener';
import { MessageTemplate }    from '@/services/MercureListener';
import { OnMessageFunction }  from '@/services/MercureListener';
import UserTokenModel         from '@models/account/UserTokenModel';
import appStore               from '@/stores/appStore';

/** Démarré une fois l'utilisateur authentifié, s'abonne au topic = urn de l'user. */
class UserMercureListener {
	private _listener?: MercureListener<UserTokenModel>;
	private _onMessage: Record<string, OnMessageFunction[]> = {};
	private _onAny: OnMessageFunction[] = [];

	/**
	 * Enregistre une fonction appelée à chaque message reçu pour un kind donné.
	 * Si la fonction renvoie false, elle est désinscrite automatiquement.
	 */
	public on(kind: string, onMessage: OnMessageFunction): void {
		(this._onMessage[kind] ??= []).push(onMessage);
	}

	/** Désinscrit manuellement une fonction préalablement enregistrée via on(). */
	public off(kind: string, onMessage: OnMessageFunction): void {
		this._onMessage[kind] = (this._onMessage[kind] ?? []).filter(fn => fn !== onMessage);
	}

	/**
	 * Enregistre une fonction appelée à chaque message reçu, quel que soit son kind.
	 * Si la fonction renvoie false, elle est désinscrite automatiquement.
	 */
	public onAny(onMessage: OnMessageFunction): void {
		this._onAny.push(onMessage);
	}

	/** Désinscrit une fonction préalablement enregistrée via onAny(). */
	public offAny(onMessage: OnMessageFunction): void {
		this._onAny = this._onAny.filter(fn => fn !== onMessage);
	}

	public async start(): Promise<void> {
		const user = appStore.user;

		if (!user.id) {
			return;
		}

		this.stop();

		this._listener = new MercureListener<UserTokenModel>({
			token: new UserTokenModel(),
			tokenAttributes: () => ({ id: user.id }),
			onMessage: async (data, e) => {
				const message = data as MessageTemplate;
				if (!message.kind) {
					return;
				}

				const onMessages = this._onMessage[message.kind] ?? [];
				await Promise.all([
					...onMessages.map(async onMessage => {
						const keepListening = await onMessage(message, e);
						if (keepListening === false) {
							this.off(message.kind, onMessage);
						}
					}),
					...this._onAny.map(async onMessage => {
						const keepListening = await onMessage(message, e);
						if (keepListening === false) {
							this.offAny(onMessage);
						}
					}),
				]);
			},
		});

		await this._listener.start([user.urn]);
	}

	/** Coupe l'écoute et libère le listener (par ex quand on déco). */
	public stop(): void {
		this._listener?.stop();
		this._listener = undefined;
	}
}

export default new UserMercureListener();
